import { getApiBase } from "./apiBase";

const WAKE_TIMEOUT_MS = 3 * 60 * 1000;
const DEFAULT_RETRY_MS = 15_000;

export type WakeResponse =
  | { status: "ready" }
  | { status: "starting"; retryAfterMs?: number }
  | { status: "error"; message?: string };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchReady(): Promise<boolean> {
  const base = getApiBase();
  if (!base) return false;
  // Ready is always on the game server (ALB), not the wake Lambda URL.
  try {
    const res = await fetch(`${base}/api/ready`, { method: "GET", cache: "no-store" });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Ensures the ECS game server is running (wake Lambda + poll until /api/ready or wake returns ready).
 * No-op in local dev when wake endpoint is missing (server already on localhost).
 */
function wakeEndpoint(): string | null {
  const direct = process.env.REACT_APP_WAKE_URL?.trim();
  if (direct) return direct.replace(/\/$/, "");
  const base = getApiBase();
  if (!base) return null;
  return `${base}/api/wake`;
}

export async function ensureServerAwake(): Promise<void> {
  const wakeUrl = wakeEndpoint();
  const apiBase = getApiBase();
  if (!wakeUrl) return;

  const deadline = Date.now() + WAKE_TIMEOUT_MS;
  let lastError: string | null = null;

  while (Date.now() < deadline) {
    try {
      const wakeRes = await fetch(wakeUrl, { method: "GET", cache: "no-store" });
      if (wakeRes.ok) {
        const body = (await wakeRes.json()) as WakeResponse;
        if (body.status === "ready") return;
        if (body.status === "error") {
          lastError = body.message ?? "Wake failed";
        } else if (body.status === "starting") {
          const waitMs = body.retryAfterMs ?? DEFAULT_RETRY_MS;
          if (await fetchReady()) return;
          await sleep(Math.min(waitMs, deadline - Date.now()));
          continue;
        }
      } else if (wakeRes.status === 404 || wakeRes.status === 403) {
        // Pre-wake deploy or local dev — assume server is already up
        if (await fetchReady()) return;
        return;
      } else {
        lastError = `Wake HTTP ${wakeRes.status}`;
      }
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
    }

    if (await fetchReady()) return;
    await sleep(DEFAULT_RETRY_MS);
  }

  throw new Error(
    lastError
      ? `Game server did not start in time (${lastError}). Try again in a minute.`
      : "Game server did not start in time. Try again in a minute."
  );
}
